import {
  getSavedSearches,
  createSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
  getSavedSearchNewListingsCount,
} from 'data/saved-searches';
import { SavedSearchRecord } from 'domain/saved-search';
import type { SavedSearchDraft, SavedSearchRepository } from 'domain/saved-search';
import type { ThemeNames } from 'types/themes';

/**
 * SavedSearchRepository
 *
 * Collection-like access to `SavedSearchRecord` aggregates. It speaks the domain
 * language — records/drafts in, records out — and hides the persistence details:
 * the DTO⇄domain mapping (`SavedSearchRecord.from`, `draft.toCreatePayload`,
 * `record.toUpdatePayload`) and the backend selection all live behind these
 * methods, so callers (the store hook) never touch `data/saved-searches` DTOs.
 *
 * A thin adapter over the existing `data/saved-searches` gateways — it does not
 * change their behavior, it just gives them a domain-shaped surface. Implements
 * the domain's `SavedSearchRepository` port.
 */
export const savedSearchRepository: SavedSearchRepository = {
  /** The `limit` most recent saved searches (newest first) as domain records. */
  async listRecents(limit: number, source: ThemeNames): Promise<SavedSearchRecord[]> {
    const { savedSearches } = await getSavedSearches(limit, undefined, source);
    return savedSearches.map(saved => SavedSearchRecord.from(saved));
  },

  /**
   * Listings that became "new" for a saved search since `since` (ISO 8601), or
   * null when the count can't be resolved.
   */
  async newListingsCount(id: string, since: string): Promise<number | null> {
    return getSavedSearchNewListingsCount(id, since);
  },

  /** Persists a new saved search from a validated draft and returns the created record. */
  async create(draft: SavedSearchDraft, source: ThemeNames): Promise<SavedSearchRecord> {
    const created = await createSavedSearch({ ...draft.toCreatePayload(), source });
    return SavedSearchRecord.from(created);
  },

  /** Persists edits to an existing saved search. */
  async update(record: SavedSearchRecord, source: ThemeNames): Promise<void> {
    await updateSavedSearch({ ...record.toUpdatePayload(), source });
  },

  /** Removes a saved search by id. */
  async remove(id: string): Promise<void> {
    await deleteSavedSearch(id);
  },
};
